import React, { type PropsWithChildren, useCallback, useEffect, useMemo, useRef } from 'react';

import memoize from 'lodash/memoize';
import type { ProductKeys } from 'packages/search/search-page/src/common/constants/products';

import { fg } from '@atlaskit/platform-feature-flags';

import type { ApplicationModes } from '../../../../../common/constants/application-modes';
import {
	type BootstrapState,
	type ClearReferrerCallback,
	initialState,
	type OnSearchCompletedCallback,
	type RefreshSearchSessionIdCallback,
	type SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import type { ThirdPartyConfigsBootstrap } from '../../../../../common/constants/schemas/3p-config';
import {
	SearchExperience,
	type SearchStoreExperience,
} from '../../../../../common/constants/search';
import { getApplicationMode } from '../../../../../common/utils/bootstrap';
import { getMultiSiteSearchInRovo } from '../../../../../common/utils/experiments';
import { isHello } from '../../../../../common/utils/is-hello';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import { type BootstrapContainerProps, type QueryParamsCallback } from '../../index';
import { StaticBootstrapContext } from '../index';

export type StaticContextProviderProps = {
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialText: string | undefined;
	initialThirdPartyConfigs: Partial<ThirdPartyConfigsBootstrap>;
	experience?: SearchStoreExperience;
};

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	| 'queryParams'
	| 'referrer'
	| 'queryParamsCallback'
	| 'searchSessionId'
	| 'smartLinkUsage'
	| 'prevQueryParams'
	| 'initialSpaceARIs'
> & {
	// Added the callback wasnt being stored in state (good) and now is in static context, and is not undefined, because we always set noop in state initialiser
	queryParamsCallback: QueryParamsCallback;
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialTextQueryParam: string | undefined;
	setThirdPartyConfigs: SetThirdPartyConfigsCallback;
	onSearchCompleted: OnSearchCompletedCallback;
	experience: SearchStoreExperience;
	applicationMode: ApplicationModes;
	multiSiteSearchInRovo: boolean;
	isHello: boolean;
};

// Attempting to build defaultstate from the same underlying definitions to ease migration to minimise migration
// omit from initialState
// eslint-disable-next-line no-unused-vars
const {
	queryParams,
	referrer,
	searchSessionId,
	thirdPartyConfigs,
	prevQueryParams,
	initialSpaceARIs,
	...inheritedInitialState
} = initialState;
export const defaultStaticContext = {
	...inheritedInitialState,
	initialTextQueryParam: undefined,
	queryParamsCallback: () => {},
	refreshSearchSessionId: () => {},
	clearReferrer: () => {},
	setThirdPartyConfigs: (() => {}) as SetThirdPartyConfigsCallback,
	onSearchCompleted: () => {},
	experience: SearchExperience.SEARCH_PAGE as SearchStoreExperience,
	applicationMode: getApplicationMode({ isRovoEnabled: inheritedInitialState.isRovoEnabled }),
	multiSiteSearchInRovo: getMultiSiteSearchInRovo(inheritedInitialState.experimentConfig),
	isHello: isHello(inheritedInitialState.cloudId),
	thirdPartyConfigs: {} as Partial<ThirdPartyConfigsBootstrap>,
};

/**
 * Conceptually this is a large single useMemo around as many pieces of state as we can guarantee do not change after the bootstrap phase (isConfigLoaded)
 */
export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParams,
	queryParamsCallback,
	refreshSearchSessionId,
	initialText,
	clearReferrer,
	initialThirdPartyConfigs,
	onSearchCompleted,
	experience,
}: PropsWithChildren<BootstrapContainerProps & StaticContextProviderProps>): React.JSX.Element => {
	/**
	 * Static lock version counter. The useMemo recomputes whenever this value changes.
	 *
	 * Starts at 0 (lock open). Incremented to 1 when bootstrap config finishes loading (lock closes).
	 * If isConfigLoaded is already true on mount, starts at 1 to avoid an extra render cycle.
	 *
	 * Post-bootstrap, the lock can be reopened by incrementing this counter (e.g. via setThirdPartyConfigs for OAuth).
	 * The useMemo will recompute on the next render, reading current values from refs.
	 * This is an expensive action that should be used sparingly and only in response to uncommon user actions
	 * Current use cases:
	 * 1. OAuth connect flow modifies 3P configs to allow an immediate search without a page refresh
	 *
	 * IMPORTANT: This counter is the SOLE dependency of the static useMemo. Do not add other dependencies.
	 * If you need the useMemo to recompute for a new value, store that value in a ref and reopen the lock by incrementing this counter.
	 */
	const [staticLockVersion, setStaticLockVersion] = React.useState(() =>
		shouldStaticLockClose({ isConfigLoaded: props.isConfigLoaded || false }) ? 1 : 0,
	);
	const staticLockClosed = staticLockVersion > 0;

	/**
	 * thirdPartyConfigs is expected to be static after bootstrap, but can be overridden during OAuth workflow as a cheap "cache bust".
	 *
	 * Stored in a ref so that updates do not directly cause re-renders. Instead, the OAuth callback
	 * (setThirdPartyConfigs) updates the ref and reopens the static lock for exactly one render,
	 * allowing the useMemo to recompute with the new value.
	 */
	const thirdPartyConfigsRef = useRef<Partial<ThirdPartyConfigsBootstrap>>(
		initialThirdPartyConfigs || {},
	);
	// Sync thirdPartyConfigs from props when config finishes loading (before static lock closes)
	if (!staticLockClosed && props.isConfigLoaded) {
		thirdPartyConfigsRef.current = initialThirdPartyConfigs || {};
	}
	const setThirdPartyConfigs: SetThirdPartyConfigsCallback = useCallback(
		(configs: Partial<ThirdPartyConfigsBootstrap>) => {
			thirdPartyConfigsRef.current = configs;
			// Reopen the static lock for exactly one render so the useMemo recomputes with the new thirdPartyConfigs
			setStaticLockVersion((v) => v + 1);
		},
		[],
	);

	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		const parsedQueryParams =
			queryParams && fg('enable_rovo_search_overrides') ? parseQueryParams(queryParams) : undefined;

		const isRovoEnabled = props.isRovoEnabled || defaultStaticContext.isRovoEnabled;
		const experimentConfig = props.experimentConfig || defaultStaticContext.experimentConfig;
		const cloudId = props.cloudId || defaultStaticContext.cloudId;
		// This can contain duplicates from e.g. a full drive connector and a drive smartlink connector
		const products = dedupProducts(props.products || [], defaultStaticContext.products);

		return {
			// Literal constant
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,

			// Static from internal props to BootstrapProvider
			experience: experience || defaultStaticContext.experience,

			// Static from host product props
			cloudId: cloudId,
			isHello: isHello(cloudId),
			aggregatorHostName: props.aggregatorHostName || defaultStaticContext.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || defaultStaticContext.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || defaultStaticContext.SAINHostName,
			orgId: props.orgId || defaultStaticContext.orgId,
			primaryProduct: props.primaryProduct || defaultStaticContext.primaryProduct,
			analyticsAttributes: props.analyticsAttributes || defaultStaticContext.analyticsAttributes,
			features: props.features || defaultStaticContext.features,
			experimentLayers: props.experimentLayers || defaultStaticContext.experimentLayers,
			queryParamsCallback: queryParamsCallback || defaultStaticContext.queryParamsCallback,
			onSearchCompleted:
				onSearchCompleted || props.onSearchCompleted || defaultStaticContext.onSearchCompleted,
			user: props.user || defaultStaticContext.user,

			// Extracted on first load from queryParams (host product props)
			initialTextQueryParam: initialText,
			// when removing this feature flag, update the type to no longer be optional
			...(fg('enable_rovo_search_overrides') && {
				overrides: parsedQueryParams?.overrides || initialState.overrides,
			}),

			// DYNAMIC: from bootstrap config -> isConfigLoaded
			isConfigLoaded: props.isConfigLoaded || defaultStaticContext.isConfigLoaded,
			intercomHmac: props.intercomHmac || defaultStaticContext.intercomHmac,
			isNounsAvailable: props.isNounsAvailable || defaultStaticContext.isNounsAvailable,
			isRovoEnabled,
			applicationMode: getApplicationMode({ isRovoEnabled }),
			nouns: props.nouns || defaultStaticContext.nouns,
			productEdition: props.productEdition || defaultStaticContext.productEdition,
			productDataRegions: props.productDataRegions || defaultStaticContext.productDataRegions,
			rovoFeatures: props.rovoFeatures || defaultStaticContext.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || defaultStaticContext.linkedCloudARIs,
			isJiraIssueIndexed: props.isJiraIssueIndexed || defaultStaticContext.isJiraIssueIndexed,
			hasEditOrViewPlanPermission:
				props.hasEditOrViewPlanPermission || defaultStaticContext.hasEditOrViewPlanPermission,
			// when removing this feature flag, update the type to no longer be optional
			...(fg('enable_rovo_search_overrides') && {
				overrides: parsedQueryParams?.overrides || initialState.overrides,
			}),

			// Third party configs - static after bootstrap, mutable only via OAuth callback (read from ref)
			thirdPartyConfigs: thirdPartyConfigsRef.current,

			// passed directly through from props
			refreshSearchSessionId,
			clearReferrer,
			setThirdPartyConfigs,

			// TODO QS-9197
			// this is powered by useExperimentConfig in packages/search/search-page/src/controllers/store/bootstrap/bootstrap-store-provider/index.tsx
			// Input props are:
			// - combinedExperimentLayer: should migrate to this static context, relies on primaryProduct (host product prop)
			// - experimentLayers: (host product props)
			// - sideBySideExperiment: should migrate to this static context, relise on features (host product prop)
			// - statsigLayer: for cleanup, no longer used (set to undefined)
			// - onError: useLogException which can be moved to this static context
			//
			// Goal is to migrate this to `getExperimentConfig` consuming the above, called from this static context
			// getExperimentConfig shouldn't need any hooks internally as it should run a single time.
			// (removing the use prefix blocks merging any side-effect containing react hooks!)
			experimentConfig,
			multiSiteSearchInRovo: getMultiSiteSearchInRovo(experimentConfig),

			// TODO QS-9384
			// This is powered by getAccessibleProducts in packages/search/search-page/src/controllers/store/bootstrap/bootstrap-store-provider/index.tsx which can be wholesale migrated here
			// - supportedProducts: allProductKeys: a literal constant
			// - products: (from host product props)
			// - accessibleProducts (from config)
			products,
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [staticLockVersion]); // Do not expand this dependency. See staticLockVersion JSDoc. Reach out to #search-plex or #search-plex-perf-squad with any questions

	/**
	 * Close the static lock _after_ the render that qualifies it to close, if it didn't already eagerly on mount. This is the code path that supports the config not being ready on the first render.
	 */
	useEffect(() => {
		if (
			!staticLockClosed &&
			shouldStaticLockClose({ isConfigLoaded: props.isConfigLoaded || false })
		) {
			setStaticLockVersion((v) => v + 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isConfigLoaded]);

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			{children}
		</StaticBootstrapContext.Provider>
	);
};

type StaticLockProps = {
	isConfigLoaded: boolean;
};

/**
 * Lock static context values after config is loaded to ensure they do not change during the session, as they are expected to be static after the bootstrap phase.
 * This also allows us to remove any dependencies on props that would cause unnecessary re-renders of the static context provider and all of its children
 *
 * The only values that should be here are booleans set by ASYNC behaviour that we must wait for (e.g. the config network call(s))
 * To add a new async dependency, provide a boolean e.g. myAsyncValue that is false until the async behaviour is complete, and then add it to the shouldStaticLockClose function.
 *
 * Note: This is on the search query critical performance path, any additional values added here should be carefully considered.
 * Reach out to #search-plex or #search-plex-perf-squad with any questions
 */
const shouldStaticLockClose = ({ isConfigLoaded }: StaticLockProps) => {
	return isConfigLoaded; // Set by `useFetchConfig`, supported by promise-reuse and localStorage caching
};

/**
 * The set of `products` as passed in as a prop has duplicates due to smartlinks etc (drive can appear several times!)
 * We uniq() this list for use within search
 */
const dedupProducts = memoize(
	(a: ProductKeys[], b: ProductKeys[]) => {
		return [...new Set([...a, ...b])];
	},
	(a: ProductKeys[], b: ProductKeys[]) => a.join('') + b.join(''),
);
